import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { filter, map } from 'rxjs';
import { ACTIVE_IDP_KEY, PENDING_IDP_KEY } from './models/auth-storage-keys';
import { AuthUserClaims } from './models/auth-user.model';
import { IdpConfigId } from './models/idp-config-id.enum';
import { AppConfigService } from '../services/app-config.service';
import { LoggingService } from '../services/logging.service';

/**
 * Authentication facade — single point of consumption of the auth state by the UI.
 *
 * Supports two parallel OIDC configurations:
 *  - 'oidc'  → PingID (primary)
 *  - 'okta'  → Okta   (fallback)
 *
 * The Ping → Okta → /login-error fallback is orchestrated by
 * LoginRelayComponent and AppComponent.handleAuthFallback().
 */
@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private readonly oidc = inject(OidcSecurityService);
  private readonly events = inject(PublicEventsService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly logger = inject(LoggingService);
  private readonly browserLocation = window.location;

  private resolveLoginUrl(configId: IdpConfigId | null = this.activeConfigId()): string {
    // Same resolution strategy used in AuthErrorComponent.
    const loginUrl =
      configId === IdpConfigId.Okta
        ? this.appConfigService.config.okta?.login_url
        : (this.appConfigService.config.oidc?.login_url ??
          this.appConfigService.config.okta?.login_url);

    return loginUrl ?? '/';
  }

  private redirectToCiamLogin(configId: IdpConfigId | null = this.activeConfigId()): void {
    this.browserLocation.assign(this.resolveLoginUrl(configId));
  }

  readonly isAuthenticated: Signal<boolean> = toSignal(
    this.oidc.isAuthenticated$.pipe(
      map(({ allConfigsAuthenticated }) => allConfigsAuthenticated.some((c) => c.isAuthenticated)),
    ),
    { initialValue: false },
  );

  readonly activeConfigId: Signal<IdpConfigId | null> = toSignal(
    this.oidc.isAuthenticated$.pipe(
      map(({ allConfigsAuthenticated }) => {
        const active = allConfigsAuthenticated.find((c) => c.isAuthenticated);
        return (active?.configId as IdpConfigId) ?? null;
      }),
    ),
    { initialValue: null },
  );

  readonly userClaims: Signal<AuthUserClaims | null> = toSignal(
    this.oidc.userData$.pipe(map(({ userData }) => (userData as AuthUserClaims) ?? null)),
    { initialValue: null },
  );

  readonly hasUserClaims = computed(() => this.userClaims() !== null);

  readonly silentRenewFailed$ = this.events
    .registerForEvents()
    .pipe(filter((e) => e.type === EventTypes.SilentRenewFailed));

  checkAuth() {
    return this.oidc.checkAuthMultiple();
  }

  getAccessToken() {
    return this.oidc.getAccessToken(this.activeConfigId() ?? undefined);
  }

  loginWith(configId: IdpConfigId, customParams?: Record<string, string | number | boolean>) {
    this.oidc.authorize(configId, customParams ? { customParams } : undefined);
  }

  /**
   * Triggers a refresh_token exchange for the given config. Useful when a silent
   * renew fails and we want to attempt one explicit retry before sending the user
   * to the error page.
   */
  forceRefreshSession(configId: IdpConfigId) {
    return this.oidc.forceRefreshSession(undefined, configId);
  }

  /**
   * Clears tokens/state of a single OIDC config from local storage, without
   * redirecting. Used to evict stale state of an IdP that the user did not
   * actually authenticate with (avoids spurious refresh attempts).
   */
  logoutLocalFor(configId: IdpConfigId) {
    this.oidc.logoffLocal(configId);
  }

  logoutLocal() {
    sessionStorage.removeItem(ACTIVE_IDP_KEY);
    this.oidc.logoffLocalMultiple();
  }

  resetAuthState() {
    sessionStorage.removeItem(ACTIVE_IDP_KEY);
    sessionStorage.removeItem(PENDING_IDP_KEY);
    this.oidc.logoffLocalMultiple();
  }

  logout() {
    const storedId = sessionStorage.getItem(ACTIVE_IDP_KEY);
    sessionStorage.removeItem(ACTIVE_IDP_KEY);
    sessionStorage.removeItem(PENDING_IDP_KEY);

    const storedConfigId: IdpConfigId | null =
      storedId === IdpConfigId.Oidc || storedId === IdpConfigId.Okta ? storedId : null;

    const activeId = this.activeConfigId() ?? storedConfigId;

    this.logger.info('[AuthFacade] Executando logout apenas localmente e redirecionando para o CIAM.');

    // 1. Destrói a sessão, tokens e dados do usuário no Angular
    this.oidc.logoffLocalMultiple();

    // 2. Redireciona imediatamente para a URL do CIAM sem passar pelo provedor
    this.redirectToCiamLogin(activeId);
  }
}
